import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtMoney, fmtDate } from "@/lib/helpers";
import { ClipboardList, CheckCircle2, Search, AlertTriangle } from "lucide-react";

export default function SupplierMarketplace() {
  const { user } = useAuth();
  const [rfqs, setRfqs] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/rfqs").then((r) => setRfqs(r.data)).catch(() => {}); }, []);
  const filtered = rfqs.filter((r) => (r.title + r.process + r.material).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-6 md:p-8 max-w-7xl">
      <div className="label-tech text-muted-foreground mb-1">Open opportunities</div>
      <h1 className="text-3xl font-bold tracking-tight">RFQ Marketplace</h1>
      <p className="mt-2 text-sm text-muted-foreground">Review incoming requests and submit competitive quotes.</p>

      {user.supplier_status !== "approved" && (
        <div className="mt-6 flex items-start gap-3 border border-amber-200 bg-amber-50 text-amber-800 rounded-sm px-4 py-3" data-testid="supplier-pending-banner">
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium">Account pending approval</div>
            Your supplier account is <span className="font-mono">{user.supplier_status}</span>. You can browse RFQs but can't submit quotes until an admin approves you.
          </div>
        </div>
      )}

      <div className="mt-6 relative max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input data-testid="marketplace-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search process, material…"
          className="w-full border border-input bg-card pl-9 pr-3 py-2.5 rounded-sm text-sm outline-none focus:border-brand transition-colors" />
      </div>

      <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.length === 0 && (
          <div className="col-span-full border border-dashed border-border rounded-sm p-12 text-center">
            <ClipboardList className="h-8 w-8 text-muted-foreground mx-auto" />
            <p className="mt-3 text-sm text-muted-foreground">No open RFQs right now. Check back soon.</p>
          </div>
        )}
        {filtered.map((r) => (
          <Link key={r.id} to={`/rfqs/${r.id}`} data-testid={`rfq-card-${r.id}`}
            className="group border border-border rounded-sm bg-card p-5 hover:border-brand transition-colors">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold leading-snug group-hover:text-brand transition-colors">{r.title}</h3>
              <StatusBadge status={r.status} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <Meta l="Process" v={r.process} />
              <Meta l="Material" v={r.material} />
              <Meta l="Quantity" v={r.quantity} />
              <Meta l="Est." v={fmtMoney(r.instant_estimate?.total_price)} />
            </div>
            <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs">
              <span className="font-mono text-muted-foreground">{r.buyer_company || r.buyer_name} · {fmtDate(r.created_at)}</span>
              {r.my_quote_submitted && <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Quoted</span>}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function Meta({ l, v }) {
  return (
    <div>
      <div className="label-tech text-muted-foreground">{l}</div>
      <div className="font-medium truncate">{v}</div>
    </div>
  );
}
